<template>
	<div class="bene-page">
		<NavMenu></NavMenu>

		<div class="location-state">{{ locationState }}</div>

		<div class="route-stage">
			<svg class="route-svg" viewBox="0 0 390 590" preserveAspectRatio="xMidYMid meet" aria-hidden="true">
				<defs>
					<linearGradient id="beneRouteGradient" x1="70" y1="455" x2="350" y2="130" gradientUnits="userSpaceOnUse">
						<stop offset="0%" stop-color="#2467ff" />
						<stop offset="45%" stop-color="#25a4ff" />
						<stop offset="100%" stop-color="#2fffd2" />
					</linearGradient>
					<filter id="beneGlow" x="-60%" y="-60%" width="220%" height="220%">
						<feGaussianBlur stdDeviation="5" result="coloredBlur" />
						<feMerge>
							<feMergeNode in="coloredBlur" />
							<feMergeNode in="SourceGraphic" />
						</feMerge>
					</filter>
				</defs>
				<path
					id="bene-route-path"
					class="route-shadow"
					d="M72 448 C122 398 153 426 181 369 C207 316 194 262 239 236 C279 213 310 239 332 188 C346 155 363 141 376 123"
				/>
				<path
					class="route-base"
					d="M72 448 C122 398 153 426 181 369 C207 316 194 262 239 236 C279 213 310 239 332 188 C346 155 363 141 376 123"
				/>
				<path
					class="route-flow"
					d="M72 448 C122 398 153 426 181 369 C207 316 194 262 239 236 C279 213 310 239 332 188 C346 155 363 141 376 123"
				/>
				<circle class="point-ring current-ring" cx="72" cy="448" r="28" />
				<circle class="point-ring target-ring" cx="376" cy="123" r="28" />
				<circle class="point-core current-core" cx="72" cy="448" r="9" />
				<circle class="point-core target-core" cx="376" cy="123" r="9" />
				<circle class="route-runner" r="6">
					<animateMotion dur="4.2s" repeatCount="indefinite">
						<mpath href="#bene-route-path" />
					</animateMotion>
				</circle>
			</svg>

			<div class="map-label label-current">
				<div>当前位置</div>
				<span>{{ currentPositionText }}</span>
			</div>
			<div class="map-label label-target">
				<div>冈仁波齐峰</div>
				<span>31.0675°N, 81.3119°E</span>
			</div>
		</div>

		<div class="distance-panel">
			<div class="distance-stack">
				<div class="distance-value">
					<span>{{ formattedDistance }}</span>
					<em>km</em>
				</div>
				<div class="distance-caption">距离另一个世界的自己</div>
			</div>
		</div>
	</div>
</template>

<script>
	import NavMenu from '../components/nav.vue';

	const KAILASH = {
		lat: 31.0675,
		lng: 81.3119,
	};

	const DEFAULT_LOCATION = {
		lat: 39.9042,
		lng: 116.4074,
	};

	export default {
		name: 'bene',
		components: {
			NavMenu,
		},
		data() {
			return {
				currentPosition: null,
				displayDistance: 0,
				locationState: '正在获取当前位置',
				watchId: null,
				animationId: null,
				geoSettled: false,
			}
		},
		computed: {
			formattedDistance() {
				return this.displayDistance.toFixed(2);
			},
			currentPositionText() {
				if (!this.currentPosition) {
					return '定位中';
				}

				return `${this.formatCoordinate(this.currentPosition.lat, 'N', 'S')}, ${this.formatCoordinate(this.currentPosition.lng, 'E', 'W')}`;
			},
		},
		mounted() {
			this.setDistance(DEFAULT_LOCATION);
			this.startLocationWatch();
		},
		beforeDestroy() {
			if (this.watchId !== null && navigator.geolocation) {
				navigator.geolocation.clearWatch(this.watchId);
			}

			if (this.animationId) {
				cancelAnimationFrame(this.animationId);
			}
		},
		methods: {
			startLocationWatch() {
				if (!navigator.geolocation) {
					this.useDefaultLocation('当前浏览器不支持定位，已显示默认参考距离');
					return;
				}

				this.watchId = navigator.geolocation.watchPosition(
					this.handlePosition,
					this.handleLocationError,
					{
						enableHighAccuracy: true,
						timeout: 12000,
						maximumAge: 60000,
					}
				);
			},
			handlePosition(position) {
				const coords = {
					lat: position.coords.latitude,
					lng: position.coords.longitude,
				};

				this.geoSettled = true;
				this.currentPosition = coords;
				this.locationState = '已根据当前位置动态计算直线距离';
				this.setDistance(coords);
			},
			handleLocationError() {
				if (this.geoSettled) {
					return;
				}

				this.useDefaultLocation('未获得定位权限，当前显示北京默认参考距离');
			},
			useDefaultLocation(message) {
				this.geoSettled = true;
				this.currentPosition = DEFAULT_LOCATION;
				this.locationState = message;
				this.setDistance(DEFAULT_LOCATION);
			},
			setDistance(from) {
				const nextDistance = this.getDistance(from, KAILASH);
				this.animateDistance(nextDistance);
			},
			getDistance(from, to) {
				const radius = 6371.0088;
				const fromLat = this.toRadians(from.lat);
				const toLat = this.toRadians(to.lat);
				const deltaLat = this.toRadians(to.lat - from.lat);
				const deltaLng = this.toRadians(to.lng - from.lng);
				const value = Math.sin(deltaLat / 2) * Math.sin(deltaLat / 2)
					+ Math.cos(fromLat) * Math.cos(toLat)
					* Math.sin(deltaLng / 2) * Math.sin(deltaLng / 2);
				const centralAngle = 2 * Math.atan2(Math.sqrt(value), Math.sqrt(1 - value));

				return radius * centralAngle;
			},
			animateDistance(nextDistance) {
				if (this.animationId) {
					cancelAnimationFrame(this.animationId);
				}

				const startValue = this.displayDistance;
				const change = nextDistance - startValue;
				const duration = 1400;
				const startTime = performance.now();

				const step = (now) => {
					const progress = Math.min((now - startTime) / duration, 1);
					const eased = 1 - Math.pow(1 - progress, 3);
					this.displayDistance = startValue + change * eased;

					if (progress < 1) {
						this.animationId = requestAnimationFrame(step);
					} else {
						this.displayDistance = nextDistance;
					}
				};

				this.animationId = requestAnimationFrame(step);
			},
			toRadians(degree) {
				return degree * Math.PI / 180;
			},
			formatCoordinate(value, positive, negative) {
				const direction = value >= 0 ? positive : negative;
				return `${Math.abs(value).toFixed(3)}°${direction}`;
			},
		}
	}
</script>

<style scoped>
	.bene-page {
		background: #000;
		color: #fff;
		width: 100%;
		height: 100vh;
		min-height: 620px;
		position: relative;
		overflow: hidden;
		--bene-content-width: min(390px, calc(100% - 48px));
	}

	.location-state {
		position: absolute;
		z-index: 2;
		top: 88px;
		left: 50%;
		width: var(--bene-content-width);
		transform: translateX(-50%);
		color: #333333;
		font-size: 12px;
		line-height: 1.4;
		text-align: center;
	}

	.route-stage {
		position: absolute;
		z-index: 1;
		inset: 118px 0 158px;
	}

	.route-svg {
		width: 100%;
		height: 100%;
		overflow: visible;
	}

	.route-shadow,
	.route-base,
	.route-flow {
		fill: none;
		stroke-linecap: round;
		stroke-linejoin: round;
	}

	.route-shadow {
		stroke: rgba(47, 255, 210, .24);
		stroke-width: 11;
		filter: url(#beneGlow);
	}

	.route-base {
		stroke: url(#beneRouteGradient);
		stroke-width: 4.5;
		filter: url(#beneGlow);
		stroke-dasharray: 620;
		stroke-dashoffset: 620;
		animation: routeDraw 1.9s ease-out forwards;
	}

	.route-flow {
		stroke: rgba(255, 255, 255, .72);
		stroke-width: 1.25;
		stroke-dasharray: 20 18;
		animation: routeFlow 1.25s linear infinite;
	}

	.point-ring {
		fill: none;
		stroke-width: 12;
		opacity: .5;
		transform-box: fill-box;
		transform-origin: center;
		animation: pointPulse 2.4s ease-out infinite;
	}

	.current-ring {
		stroke: #2467ff;
	}

	.target-ring {
		stroke: #2fffd2;
		animation-delay: .45s;
	}

	.point-core {
		filter: url(#beneGlow);
	}

	.current-core {
		fill: #2467ff;
	}

	.target-core {
		fill: #2fffd2;
	}

	.route-runner {
		fill: #fff;
		filter: url(#beneGlow);
		opacity: .9;
	}

	.map-label {
		position: absolute;
		z-index: 2;
		padding: 8px 10px;
		border-radius: 8px;
		background: rgba(7, 13, 23, .58);
		color: #fff;
		text-align: left;
		box-shadow: 0 10px 24px rgba(0, 0, 0, .18);
		backdrop-filter: blur(8px);
	}

	.map-label div {
		font-size: 13px;
		line-height: 1.2;
	}

	.map-label span {
		display: block;
		margin-top: 3px;
		color: rgba(255, 255, 255, .58);
		font-size: 10px;
		white-space: nowrap;
	}

	.label-current {
		left: 12%;
		bottom: 14%;
	}

	.label-target {
		right: 4%;
		top: 3%;
	}

	.distance-panel {
		position: absolute;
		z-index: 2;
		left: 0;
		right: 0;
		bottom: 34px;
		text-align: center;
		padding: 0 24px;
	}

	.distance-stack {
		display: inline-block;
		text-align: left;
	}

	.distance-value {
		display: flex;
		align-items: baseline;
		justify-content: flex-start;
		gap: 10px;
		font-weight: 700;
		color: #fff;
		text-shadow: 0 10px 30px rgba(0, 0, 0, .45);
	}

	.distance-value span {
		font-size: clamp(54px, 18vw, 92px);
		line-height: .92;
		letter-spacing: 0;
	}

	.distance-value em {
		font-size: 20px;
		font-style: normal;
	}

	.distance-caption {
		margin-top: 14px;
		color: rgba(255, 255, 255, .64);
		font-size: 16px;
		letter-spacing: 0;
		text-align: left;
	}

	@keyframes routeDraw {
		to {
			stroke-dashoffset: 0;
		}
	}

	@keyframes routeFlow {
		to {
			stroke-dashoffset: -38;
		}
	}

	@keyframes pointPulse {
		0% {
			opacity: .66;
			transform: scale(.72);
		}
		70% {
			opacity: .08;
			transform: scale(1.48);
		}
		100% {
			opacity: 0;
			transform: scale(1.64);
		}
	}

	@media (max-width: 360px) {
		.route-stage {
			inset: 112px 0 148px;
		}

	}

	@media (min-width: 700px) {
		.bene-page {
			min-height: 720px;
		}

		.route-stage {
			left: 50%;
			width: min(620px, 58vw);
			transform: translateX(-50%);
		}

		.distance-panel {
			bottom: 42px;
		}
	}
</style>
